import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Confidence, ItemType } from 'common/api/resources/inbox';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import RadioButtonGroup from 'common/inputs/RadioButtonGroup';
import AdminPageSidebar from 'common/subdomain/admin/AdminPageSidebar';
import AdminSidebarBoardSection from 'common/subdomain/admin/AdminSidebarBoardSection';
import AdminSidebarMenuSection from 'common/subdomain/admin/AdminSidebarMenuSection';
import AdminSidebarSection from 'common/subdomain/admin/AdminSidebarSection';

import type { Board } from 'common/api/endpoints/boards';

import 'css/components/subdomain/admin/AdminInbox/_AdminInboxSidebar.scss';

type Props = {
  boards: Board[];
  inboxTotalItemCount: number;
};

const ConfidenceFilters = {
  all: {
    name: Confidence.all,
    render: 'All',
  },
  lowConfidence: {
    name: Confidence.lowConfidence,
    render: 'Low confidence only',
  },
  highConfidence: {
    name: Confidence.highConfidence,
    render: 'High confidence only',
  },
};

const ItemTypeFilters = {
  all: {
    name: ItemType.all,
    render: 'All',
  },
  comment: {
    name: ItemType.comment,
    render: 'Comments Only',
  },
  post: {
    name: ItemType.post,
    render: 'Posts Only',
  },
};

const AdminInboxSidebar = ({ boards, inboxTotalItemCount }: Props) => {
  // Contexts
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);

  // State
  const [boardURLNames, setBoardURLNames] = useState<string[]>(
    location.query.boardURLNames?.split('_') ?? boards.map((board) => board.urlName)
  );
  const [confidence, setConfidence] = useState<Confidence>(
    location.query.confidence ?? ConfidenceFilters.all.name
  );
  const [itemType, setItemType] = useState<ItemType>(
    location.query.itemType ?? ItemTypeFilters.all.name
  );

  // helpers
  const queryNeedsUpdate = useCallback(() => {
    const boardsNotMatching = location.query.boardURLNames !== boardURLNames.join('_');
    const confidenceNotMatching = location.query.confidence !== confidence;
    const itemTypeNotMatching = location.query.itemType !== itemType;

    return boardsNotMatching || itemTypeNotMatching || confidenceNotMatching;
  }, [boardURLNames, confidence, itemType, location.query]);

  const onBoardsChange = (boardURLNamesMap: Record<string, boolean>) => {
    const selectedBoardURLNames = Object.entries(boardURLNamesMap)
      .filter(([, selected]) => selected)
      .map(([urlName]) => urlName);

    setBoardURLNames(selectedBoardURLNames);
  };

  const onConfidenceChange = (type: { name: Confidence; render: string }) => {
    setConfidence(type.name);
  };

  const onItemTypeChange = (type: { name: ItemType; render: string }) => {
    setItemType(type.name);
  };

  // effects
  useEffect(() => {
    const shouldUpdate = queryNeedsUpdate();

    if (!shouldUpdate) {
      return;
    }

    router.replace({
      ...location,
      query: {
        ...location.query,
        boardURLNames: boardURLNames.join('_'),
        confidence,
        itemType,
      },
    });
  }, [boardURLNames, confidence, itemType, location, queryNeedsUpdate, router]);

  // renderers
  const renderBoardFilter = () => {
    const selectedBoards = boards.filter((board) => boardURLNames.includes(board.urlName));

    return (
      <AdminSidebarBoardSection
        boards={boards}
        onBoardsChange={onBoardsChange}
        selectedBoards={selectedBoards}
      />
    );
  };

  const renderConfidenceFilter = () => {
    const buttons = Object.values(ConfidenceFilters);

    return (
      <AdminSidebarSection title="Confidence Level">
        <RadioButtonGroup
          buttons={buttons}
          defaultSelectedName={confidence}
          onChange={onConfidenceChange}
        />
      </AdminSidebarSection>
    );
  };

  const renderItemTypeFilter = () => {
    const buttons = Object.values(ItemTypeFilters);

    return (
      <AdminSidebarSection title="Type">
        <RadioButtonGroup
          buttons={buttons}
          defaultSelectedName={itemType}
          onChange={onItemTypeChange}
        />
      </AdminSidebarSection>
    );
  };

  return (
    <div className="adminInboxSidebar">
      <AdminPageSidebar showSidebar={true}>
        <AdminSidebarMenuSection inboxTotalItemCount={inboxTotalItemCount} />
        <AdminSidebarSection className="headingSection">
          <p className="filtersHeading">Filters</p>
        </AdminSidebarSection>
        {renderItemTypeFilter()}
        {renderConfidenceFilter()}
        {renderBoardFilter()}
      </AdminPageSidebar>
    </div>
  );
};

export default AdminInboxSidebar;
